/**
 * SVG Path Layer Renderer
 * Renders complex vector paths using SVG path commands
 */

import React, { useId } from 'react';
import type { SvgPathLayer } from '@/types/layers';
import { svgCommandsToPath } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import type { Brush, GradientStop } from '@/types/brush';

// Generate SVG gradient definitions for a brush
function useBrushDefs(brush: Brush | undefined, id: string): { fill: string; defs: React.ReactNode } {
  if (!brush || brush.type === 'transparent') {
    return { fill: 'none', defs: null };
  }

  if (brush.type === 'solid') {
    return { fill: brush.color, defs: null };
  }

  if (brush.type === 'linear-gradient') {
    const gradId = `lg-${id}`;
    const angle = brush.angle ?? 180;
    const rad = (angle - 90) * (Math.PI / 180);
    const x1 = 50 + 50 * Math.cos(rad + Math.PI);
    const y1 = 50 + 50 * Math.sin(rad + Math.PI);
    const x2 = 50 + 50 * Math.cos(rad);
    const y2 = 50 + 50 * Math.sin(rad);

    const defs = (
      <defs>
        <linearGradient id={gradId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>
          {brush.stops.map((stop: GradientStop, i: number) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
    );
    return { fill: `url(#${gradId})`, defs };
  }

  if (brush.type === 'radial-gradient') {
    const gradId = `rg-${id}`;
    const defs = (
      <defs>
        <radialGradient id={gradId} cx={`${brush.centerX}%`} cy={`${brush.centerY}%`} r="50%">
          {brush.stops.map((stop: GradientStop, i: number) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
          ))}
        </radialGradient>
      </defs>
    );
    return { fill: `url(#${gradId})`, defs };
  }

  // SVG doesn't natively support conic/sweep gradients - fall back to first color
  if (brush.type === 'sweep-gradient') {
    return { fill: brush.stops[0]?.color ?? 'none', defs: null };
  }

  return { fill: 'none', defs: null };
}

function brushToStroke(brush: Brush | undefined): string {
  if (!brush || brush.type === 'transparent') return 'none';
  if (brush.type === 'solid') return brush.color;
  if (brush.type === 'linear-gradient' || brush.type === 'radial-gradient' || brush.type === 'sweep-gradient') {
    return brush.stops[0]?.color ?? '#000000';
  }
  return 'none';
}

interface SvgPathLayerRendererProps {
  layer: SvgPathLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function SvgPathLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: SvgPathLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const {
    commands,
    fill,
    fillRule,
    stroke,
    strokeWidth,
    strokeLinecap,
    strokeLinejoin,
    strokeDasharray,
    strokeDashoffset,
  } = content;

  // Convert commands to SVG path data
  const pathData = svgCommandsToPath(commands);

  // Calculate bounds from commands if width/height not specified
  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 100);
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 100);

  // Use unique ID for gradient definitions
  const uniqueId = useId();
  const { fill: fillValue, defs: fillDefs } = useBrushDefs(fill, `fill-${uniqueId}`);
  const strokeValue = brushToStroke(stroke);

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
    overflow: 'visible',
  };

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}
        style={{ overflow: 'visible' }}
      >
        {fillDefs}
        <path
          d={pathData}
          fill={fillValue}
          fillRule={fillRule}
          stroke={strokeValue}
          strokeWidth={strokeWidth}
          strokeLinecap={strokeLinecap}
          strokeLinejoin={strokeLinejoin}
          strokeDasharray={strokeDasharray?.join(' ')}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
    </div>
  );
}
